/** Search movement with exit prediction and memory ring buffer for tracking investigated positions. */
import { findPath } from '../utils/Pathfinder';
import { distance, distanceSq, worldToTile, tileToWorld, randomAngle, angleToDirection, directionToAngle, randomInRange } from '../utils/Math';
import type { PathHelper } from './PathHelper';
import type { Position } from '../utils/Types';
import {
  MovementResult, ZERO_MOVEMENT,
  SEARCH_MEMORY_SIZE, SEARCH_MEMORY_DIST_SQ,
  EXIT_SCAN_RADIUS, EXIT_MIN_DIST_SQ,
  EXIT_BASE_CANDIDATES, EXIT_BONUS_CANDIDATES,
  SEARCH_BASE_LEGS, SEARCH_BONUS_LEGS,
  tileBlocksMovement,
} from './constants';

/**
 * Manages search behavior: intersection-seeking movement, exit prediction,
 * and search memory to avoid revisiting areas.
 */
export class SearchSystem {
  // Search movement
  private searchDirX: number = 0;
  private searchDirY: number = 0;
  private searchLegs: number = 0;
  private searchPath: Position[] | null = null;
  private searchPathIndex: number = 0;

  // Exit prediction
  private predictedExits: Position[] = [];
  private _hasPredicted: boolean = false;
  private _predictedFromX: number = 0;
  private _predictedFromY: number = 0;

  // Search memory ring buffer
  private searchMemory: Position[] = [];

  private readonly groundLayer: Phaser.Tilemaps.TilemapLayer;
  private readonly pathHelper: PathHelper;

  constructor(groundLayer: Phaser.Tilemaps.TilemapLayer, pathHelper: PathHelper) {
    this.groundLayer = groundLayer;
    this.pathHelper = pathHelper;
  }

  get hasPredicted(): boolean { return this._hasPredicted; }
  get predictedFromX(): number { return this._predictedFromX; }
  get predictedFromY(): number { return this._predictedFromY; }

  /** Search memory ring buffer positions. For debug visualization. */
  get currentSearchMemory(): readonly Position[] { return this.searchMemory; }

  /** Clear all search state for a fresh chase. */
  resetForChase(): void {
    this._hasPredicted = false;
    this.searchMemory.length = 0;
    this.searchPath = null;
    this.predictedExits = [];
  }

  /** Clear search memory only (on full aggro decay). */
  clearMemory(): void {
    this.searchMemory.length = 0;
  }

  /** Record a position in search memory (used by chase arrival). */
  recordPosition(x: number, y: number): void {
    if (this.searchMemory.length >= SEARCH_MEMORY_SIZE) {
      this.searchMemory.shift();
    }
    this.searchMemory.push({ x, y });
  }

  /**
   * Initialize search state from chase. Sets direction based on player's last movement.
   * Returns log messages.
   */
  beginSearch(entityX: number, entityY: number, playerDirX: number, playerDirY: number): string[] {
    this.searchLegs = 0;
    this.searchPath = null;
    this.searchPathIndex = 0;

    const hasMeaningfulDir = Math.abs(playerDirX) > 0.01 || Math.abs(playerDirY) > 0.01;
    if (hasMeaningfulDir) {
      this.searchDirX = playerDirX;
      this.searchDirY = playerDirY;
    } else {
      const dir = angleToDirection(randomAngle());
      this.searchDirX = dir.x;
      this.searchDirY = dir.y;
    }

    // Seed memory with starting position
    this.recordPosition(entityX, entityY);

    const logs: string[] = [];
    const memStr = this.searchMemory.map((m, i) => `[${i}](${Math.round(m.x)},${Math.round(m.y)})`).join(' ');
    logs.push(`BEGIN SEARCH dir=(${this.searchDirX.toFixed(2)},${this.searchDirY.toFixed(2)}) from=(${Math.round(entityX)},${Math.round(entityY)}) memory=${this.searchMemory.length}: ${memStr}`);

    logs.push(...this.pickSearchTarget(entityX, entityY));
    return logs;
  }

  /**
   * Follow search path, pick new legs when needed.
   * Returns movement + log messages.
   */
  searchMovement(entityX: number, entityY: number, aggroHWM: number, moveSpeed: number): { movement: MovementResult; logs: string[] } {
    const logs: string[] = [];

    if (this.searchPath && this.searchPath.length > 0) {
      const result = this.pathHelper.followWaypoints(this.searchPath, this.searchPathIndex, entityX, entityY, moveSpeed);
      this.searchPathIndex = result.newIndex;

      if (!result.arrived) {
        return { movement: { vx: result.vx, vy: result.vy, wantsAttack: false }, logs };
      }
    }

    // Reached target — pick next leg
    const hwmRatio = aggroHWM / 100;
    const maxLegs = SEARCH_BASE_LEGS + Math.floor(hwmRatio * SEARCH_BONUS_LEGS);
    this.searchLegs++;
    if (this.searchLegs >= maxLegs) {
      logs.push(`SEARCH max legs (${maxLegs}) — randomizing direction, keep searching`);
      this.searchLegs = 0;
      const dir = angleToDirection(randomAngle());
      this.searchDirX = dir.x;
      this.searchDirY = dir.y;
    }

    const memStr = this.searchMemory.map((m, i) => `[${i}](${Math.round(m.x)},${Math.round(m.y)})`).join(' ');
    logs.push(`SEARCH leg ${this.searchLegs}/${maxLegs} at (${Math.round(entityX)},${Math.round(entityY)}) memory=${this.searchMemory.length}: ${memStr}`);
    logs.push(...this.pickSearchTarget(entityX, entityY));
    return { movement: ZERO_MOVEMENT, logs };
  }

  /**
   * Scan for exits near a position, rank by player direction, queue for investigation.
   * Returns log messages.
   */
  predictPlayerExit(fromX: number, fromY: number, playerDirX: number, playerDirY: number, aggroHWM: number): { beginSearch: boolean; logs: string[] } {
    const allExits = this.findNearbyExits(fromX, fromY);
    const logs: string[] = [];

    // Filter out exits too close to previously visited positions
    const exits = allExits.filter(exit => {
      return !this.searchMemory.some(m => distanceSq(exit.x, exit.y, m.x, m.y) < SEARCH_MEMORY_DIST_SQ);
    });

    if (allExits.length > 0 && exits.length === 0) {
      logs.push(`PREDICT ${allExits.length} exits near (${Math.round(fromX)},${Math.round(fromY)}) all excluded by memory → search`);
      return { beginSearch: true, logs };
    }

    if (exits.length === 0) {
      logs.push(`PREDICT no exits near (${Math.round(fromX)},${Math.round(fromY)}) → search`);
      return { beginSearch: true, logs };
    }

    this._hasPredicted = true;
    this._predictedFromX = fromX;
    this._predictedFromY = fromY;

    const hasMeaningfulDir = Math.abs(playerDirX) > 0.01 || Math.abs(playerDirY) > 0.01;
    const scored = exits.map(exit => {
      const edx = exit.x - fromX;
      const edy = exit.y - fromY;
      const eDist = distance(fromX, fromY, exit.x, exit.y) || 1;
      const score = hasMeaningfulDir
        ? (edx / eDist) * playerDirX + (edy / eDist) * playerDirY
        : -eDist;
      return { ...exit, score };
    });

    scored.sort((a, b) => b.score - a.score);

    const hwmRatio = aggroHWM / 100;
    const maxCandidates = EXIT_BASE_CANDIDATES + Math.floor(hwmRatio * EXIT_BONUS_CANDIDATES);
    this.predictedExits = scored.slice(0, maxCandidates);
    logs.push(`PREDICT ${this.predictedExits.length} exits ranked (dir=${playerDirX.toFixed(2)},${playerDirY.toFixed(2)}): ${this.predictedExits.map((e, i) => `#${i + 1}(${Math.round(e.x)},${Math.round(e.y)} s=${scored[i].score.toFixed(2)})`).join(' ')}`);

    return { beginSearch: false, logs };
  }

  /**
   * Pop next predicted exit. Returns the exit position, or null if exhausted.
   * Also returns log message.
   */
  advanceToPredictedExit(): { exit: Position | null; log: string } {
    if (this.predictedExits.length === 0) {
      return { exit: null, log: `PREDICT exhausted all exits → search` };
    }

    const next = this.predictedExits.shift()!;
    return {
      exit: next,
      log: `PREDICT → exit (${Math.round(next.x)},${Math.round(next.y)}), ${this.predictedExits.length} remaining`,
    };
  }

  private pickSearchTarget(fromX: number, fromY: number, retried: boolean = false): string[] {
    const logs: string[] = [];
    const tx = worldToTile(fromX);
    const ty = worldToTile(fromY);
    const baseAngle = directionToAngle(this.searchDirX, this.searchDirY);
    const minTiles = 4;
    const maxTiles = 8;
    const maxAttempts = 6;

    for (let i = 0; i < maxAttempts; i++) {
      const angle = i < 3
        ? baseAngle + randomInRange(-Math.PI / 4, Math.PI / 4)
        : randomAngle();

      const dist = randomInRange(minTiles, maxTiles);
      const targetTX = tx + Math.round(Math.cos(angle) * dist);
      const targetTY = ty + Math.round(Math.sin(angle) * dist);

      const tile = this.groundLayer.getTileAt(targetTX, targetTY);
      if (tileBlocksMovement(tile)) continue;

      const targetX = tileToWorld(targetTX);
      const targetY = tileToWorld(targetTY);

      // Reject targets too close to recently searched positions
      let rejectIdx = -1;
      const tooClose = this.searchMemory.some((m, idx) => {
        if (distanceSq(targetX, targetY, m.x, m.y) < SEARCH_MEMORY_DIST_SQ) {
          rejectIdx = idx;
          return true;
        }
        return false;
      });
      if (tooClose) {
        const rm = this.searchMemory[rejectIdx];
        logs.push(`SEARCH REJECT (${Math.round(targetX)},${Math.round(targetY)}) — too close to memory[${rejectIdx}]=(${Math.round(rm.x)},${Math.round(rm.y)})`);
        continue;
      }

      const { path } = findPath(this.groundLayer, fromX, fromY, targetX, targetY);
      if (path && path.length > 0) {
        this.searchPath = path;
        this.searchPathIndex = 0;
        this.searchDirX = Math.cos(angle);
        this.searchDirY = Math.sin(angle);
        this.recordPosition(targetX, targetY);
        logs.push(`SEARCH TARGET → (${Math.round(targetX)},${Math.round(targetY)}) dist=${Math.round(dist)}tiles path=${path.length} waypoints memory=${this.searchMemory.length}`);
        return logs;
      }
    }

    // No viable target — shed oldest half of memory and retry once
    if (!retried && this.searchMemory.length > 1) {
      const shed = Math.ceil(this.searchMemory.length / 2);
      this.searchMemory.splice(0, shed);
      logs.push(`SEARCH no viable target — shed ${shed} memories (${this.searchMemory.length} remain), retrying`);
      logs.push(...this.pickSearchTarget(fromX, fromY, true));
      return logs;
    }

    logs.push(`SEARCH no viable target after ${maxAttempts} attempts${retried ? ' (after memory shed)' : ''}`);
    this.searchPath = null;
    return logs;
  }

  private findNearbyExits(centerX: number, centerY: number): Position[] {
    const cx = worldToTile(centerX);
    const cy = worldToTile(centerY);
    const exits: Position[] = [];

    for (let dy = -EXIT_SCAN_RADIUS; dy <= EXIT_SCAN_RADIUS; dy++) {
      for (let dx = -EXIT_SCAN_RADIUS; dx <= EXIT_SCAN_RADIUS; dx++) {
        const ttx = cx + dx;
        const tty = cy + dy;

        const tile = this.groundLayer.getTileAt(ttx, tty);
        if (tileBlocksMovement(tile)) continue;

        const worldX = tileToWorld(ttx);
        const worldY = tileToWorld(tty);

        if (distanceSq(centerX, centerY, worldX, worldY) < EXIT_MIN_DIST_SQ) continue;

        let openCount = 0;
        let openMask = 0;
        const cardinals: [number, number, number][] = [[0, -1, 1], [0, 1, 2], [1, 0, 4], [-1, 0, 8]];
        for (const [ndx, ndy, bit] of cardinals) {
          const neighbor = this.groundLayer.getTileAt(ttx + ndx, tty + ndy);
          if (!tileBlocksMovement(neighbor)) {
            openCount++;
            openMask |= bit;
          }
        }

        if (openCount >= 3) {
          exits.push({ x: worldX, y: worldY });
          continue;
        }

        if (openCount === 2 && openMask !== 3 && openMask !== 12) {
          exits.push({ x: worldX, y: worldY });
        }
      }
    }

    return exits;
  }
}
